import { useCallback, useRef, useState } from 'react';
import { getOperateType, IFramework, useMoveEventListener } from '@/framework';
import { ILayerProps, LayerType } from '@/version-types';
import { applyCropOperation, createGestureContext, CropGestureContext, resolveOperateType } from '../core/operations';
import { CropPointerEvent } from '../core/touch-event';
import type { CropImgPlugin } from '../crop-img';
import { CropSession } from '../session/crop-session';

export const useCropInteraction = (framework: IFramework, stageScale: number, isAutoCutoutLayer: boolean) => {
    const sessionRef = useRef<CropSession | null>(null);
    const gestureRef = useRef<CropGestureContext | null>(null);
    const unsubscribeRef = useRef<(() => void) | null>(null);
    const [previewTick, setPreviewTick] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const bindSession = useCallback((session: CropSession | null) => {
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;
        sessionRef.current = session;
        setIsDragging(!!session);

        if (session) {
            unsubscribeRef.current = session.subscribe(() => {
                setPreviewTick((tick) => tick + 1);
            });
        }

        setPreviewTick((tick) => tick + 1);
    }, []);

    const getPreviewLayer = useCallback((fallback: ILayerProps | null) => {
        return sessionRef.current?.preview ?? fallback;
    }, []);

    useMoveEventListener(
        (event: CropPointerEvent) => {
            const cropImgPlugin = framework.service.registration.getInstance<CropImgPlugin>('CropImgPlugin');
            if (event.isStart) {
                const layer = cropImgPlugin.getCurrentLayer();
                if (!layer || layer.layerType !== LayerType.IMAGE_LAYER) return;

                const operateType = resolveOperateType(event.target.getAttribute('data-operate-type'));
                if (!operateType || !getOperateType(event.target)) return;

                const session = new CropSession(layer as ILayerProps);
                gestureRef.current = createGestureContext(
                    session.origin,
                    operateType,
                    stageScale,
                    isAutoCutoutLayer,
                    event,
                );
                bindSession(session);
                return;
            }

            if (event.isEnd) {
                const session = sessionRef.current;
                if (session) {
                    const committedLayer = session.commit();
                    cropImgPlugin.updateCurrentLayer(committedLayer);
                }
                gestureRef.current = null;
                bindSession(null);
                return;
            }

            const session = sessionRef.current;
            const gesture = gestureRef.current;
            if (!session || !gesture) return;

            const nextLayer = applyCropOperation(gesture, event, session.preview);
            if (nextLayer) {
                session.setPreview(nextLayer);
            }
        },
        undefined,
        'crop-img-interaction',
    );

    return { getPreviewLayer, previewTick, isDragging };
};
